import { useState } from 'react';
import type { AccessGrant, AlbumDetail } from '@photos/shared';
import { useAccessCandidates, useAccessChange, useAccessGrants, useCreateInvite, useInvites, useRevokeInvite } from '../../api/hooks';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { avatarColor, fmtShortDate, initials } from '../../lib/format';
import { useToast } from '../../state/toasts';

/** "Invite link by Daniel Kim · Jul 3" / "Added by Minsu Son · Jul 3". */
export function grantedLine(g: AccessGrant): string {
  const how = g.how === 'invite' ? 'Invite link' : 'Added';
  return `${how}${g.grantedByName ? ` by ${g.grantedByName}` : ''} · ${fmtShortDate(new Date(g.grantedAt))}`;
}

/** Confirm, then take away someone's access to one album. */
export function RemoveAccessDialog({ grant, onClose }: { grant: AccessGrant; onClose(): void }) {
  const change = useAccessChange();
  const toast = useToast();
  const remove = () =>
    change.mutate(
      { userId: grant.userId, albumId: grant.albumId, action: 'remove' },
      {
        onSuccess: () => {
          onClose();
          toast({ icon: 'check', title: `${grant.userName} removed from ${grant.albumName}` });
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t remove access', sub: e.message }),
      },
    );
  return (
    <Modal onClose={onClose} labelledBy="remove-access-title">
      <h3 id="remove-access-title">
        Remove {grant.userName} from {grant.albumName}?
      </h3>
      <p className="help">They won’t be able to open this album in Studio any more; photos they uploaded stay.</p>
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={change.isPending} onClick={remove}>
          Remove access
        </button>
      </div>
    </Modal>
  );
}

/** Search active users (type user) and add one to an album in one click. */
export function AddPeople({ albumId, albumName }: { albumId: string; albumName: string }) {
  const [q, setQ] = useState('');
  const term = q.trim();
  const candidates = useAccessCandidates(term, albumId, term.length > 0);
  const change = useAccessChange();
  const toast = useToast();
  const add = (u: { id: string; name: string }) =>
    change.mutate(
      { userId: u.id, albumId, action: 'add' },
      {
        onSuccess: () => toast({ icon: 'check', title: `${u.name} can now upload and tag in ${albumName}` }),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t add access', sub: e.message }),
      },
    );
  return (
    <div className="field">
      <input className="inp" type="search" placeholder="Find people by name, email or church" aria-label="Find people to add" value={q} onChange={(e) => setQ(e.target.value)} />
      {term && (
        <div style={{ display: 'grid', gap: 2, marginTop: 6 }}>
          {(candidates.data?.items ?? []).map((u) => (
            <div className="who" key={u.id} style={{ padding: '4px 0' }}>
              <span className="av" style={{ background: avatarColor(u.name) }}>
                {initials(u.name)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {u.name}
                <small>{u.email}</small>
              </div>
              <button type="button" className="btn sm" aria-label={`Add ${u.name}`} disabled={change.isPending} onClick={() => add(u)}>
                <Icon name="plus" />
                Add
              </button>
            </div>
          ))}
          {candidates.data && !candidates.data.items.length && (
            <small style={{ color: 'var(--muted)' }}>No one found. Media already have every album; pending and suspended people can’t be added.</small>
          )}
        </div>
      )}
    </div>
  );
}

/** Invite links for an album: create (7, 30 or 90 days), copy, revoke. */
function InviteLinks({ album }: { album: AlbumDetail }) {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const invites = useInvites(album.id, album.can.manage);
  const create = useCreateInvite(album.id);
  const revoke = useRevokeInvite();
  const toast = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* the link stays visible to copy by hand */
    }
    setCopied(url);
  };
  const fail = (e: Error) => toast({ icon: 'info', title: 'That didn’t work', sub: e.message });
  return (
    <>
      <p className="help" style={{ margin: '0 0 8px' }}>
        Anyone signed in who opens the link gets access to this album: they can upload and tag here only.
      </p>
      <div className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select className="inp" aria-label="Link expires" value={days} onChange={(e) => setDays(Number(e.target.value) as 7 | 30 | 90)} style={{ width: 'auto' }}>
          <option value={7}>Expires in 7 days</option>
          <option value={30}>Expires in 30 days</option>
          <option value={90}>Expires in 90 days</option>
        </select>
        <button type="button" className="btn" disabled={create.isPending} onClick={() => create.mutate(days, { onSuccess: (i) => void copy(i.url), onError: fail })}>
          <Icon name="share" />
          Create link
        </button>
      </div>
      {(invites.data ?? []).map((i) => (
        <div className="field" key={i.id}>
          <div className="copy">
            <input className="inp" readOnly value={i.url} aria-label="Invite link" onFocus={(e) => e.target.select()} />
            <button type="button" className="btn" onClick={() => void copy(i.url)}>
              {copied === i.url ? 'Copied' : 'Copy'}
            </button>
            <button type="button" className="btn ghost" onClick={() => revoke.mutate(i.id, { onError: fail })} disabled={revoke.isPending}>
              Revoke
            </button>
          </div>
          <small style={{ color: 'var(--muted)' }}>
            Created by {i.createdByName} · expires {fmtShortDate(new Date(i.expiresAt))}
          </small>
        </div>
      ))}
    </>
  );
}

/** Album page "People" drawer: who has access (and how), add people, invite links. Media and admins. */
export function AlbumPeople({ album, onClose }: { album: AlbumDetail; onClose(): void }) {
  const grants = useAccessGrants({ albumId: album.id });
  const [removing, setRemoving] = useState<AccessGrant | null>(null);
  const items = grants.data?.items ?? [];
  return (
    <aside className="drawer" aria-label={`People in ${album.name}`}>
      <div className="drawer-head">
        <b>People</b>
        <button type="button" className="btn ghost sm" aria-label="Close people" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <p className="help" style={{ margin: '0 0 8px' }}>
        These people can upload and tag in this album only. Media have every album.
      </p>
      {items.map((g) => (
        <div className="who" key={g.userId} style={{ padding: '4px 0' }}>
          <span className="av" style={{ background: avatarColor(g.userName) }}>
            {initials(g.userName)}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {g.userName}
            <small>{grantedLine(g)}</small>
          </div>
          <button type="button" className="btn sm ghost" aria-label={`Remove ${g.userName} from ${album.name}`} onClick={() => setRemoving(g)}>
            <Icon name="x" />
          </button>
        </div>
      ))}
      {grants.data && !items.length && (
        <p className="help" style={{ margin: 0 }}>
          No one has been given this album yet.
        </p>
      )}
      <div className="sl" style={{ padding: '12px 0 6px' }}>
        Add people
      </div>
      <AddPeople albumId={album.id} albumName={album.name} />
      {album.can.manage && (
        <>
          <div className="sl" style={{ padding: '12px 0 6px' }}>
            Invite links
          </div>
          <InviteLinks album={album} />
        </>
      )}
      {removing && <RemoveAccessDialog grant={removing} onClose={() => setRemoving(null)} />}
    </aside>
  );
}
